import React, { useRef } from 'react';

const MIN_HEIGHT = 120;

const KEYBOARD_STEP = 40;

// Drag-to-resize for the preview boxes, mirroring the handle TinyMCE puts in its
// status bar. Native `resize: vertical` would be shorter, but its grip sits
// inside the scrolling text and cannot be styled or reached from the keyboard.
export function useVerticalResize() {
  const ref = useRef<HTMLDivElement | null>(null);

  const resizeBy = (delta: number) => {
    const box = ref.current;
    if (box) {
      box.style.height = `${Math.max(MIN_HEIGHT, box.offsetHeight + delta)}px`;
    }
  };

  const onPointerDown = (event: React.PointerEvent<HTMLElement>) => {
    const box = ref.current;
    if (!box || event.button !== 0) {
      return;
    }

    const handle = event.currentTarget;
    const startY = event.clientY;
    const startHeight = box.offsetHeight;

    const move = (moveEvent: PointerEvent) => {
      box.style.height = `${Math.max(
        MIN_HEIGHT,
        startHeight + moveEvent.clientY - startY,
      )}px`;
    };

    const stop = () => {
      handle.removeEventListener('pointermove', move);
      handle.removeEventListener('pointerup', stop);
      handle.removeEventListener('pointercancel', stop);
    };

    // The capture keeps the pointer events coming while the cursor runs ahead of
    // the box, which it does on every fast drag.
    handle.setPointerCapture(event.pointerId);
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', stop);
    handle.addEventListener('pointercancel', stop);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }
    event.preventDefault();
    resizeBy(event.key === 'ArrowDown' ? KEYBOARD_STEP : -KEYBOARD_STEP);
  };

  return { ref, handleProps: { onPointerDown, onKeyDown } };
}
